package com.arena_web.ui.sections.news

import androidx.compose.runtime.Composable
import com.arena_web.data.dto.NewsArticleDto
import com.arena_web.ui.components.navigation.NavAnchor
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.theme.AppColors
import com.arena_web.ui.theme.AppGradients
import com.arena_web.ui.theme.bodyLMedium
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img

// =============================================================================
// Arena News — article detail (/news/{id}). Cover hero, meta, body paragraphs,
// related articles. Spec: docs/superpowers/specs/2026-09-14-arena-news-design.md.
// =============================================================================

@Composable
fun NewsArticleDetail(
    article: NewsArticleDto,
    related: List<NewsArticleDto>,
    mobile: Boolean,
) {
    val t = LocalAppStrings.current
    val ctx = rememberPageContext()
    Div(
        attrs = {
            style {
                property("width", "100%")
                property("flex-grow", "1")
                property("min-height", "0")
                property("overflow-y", "auto")
                property("overflow-x", "hidden")
                property("scrollbar-width", "none")
            }
        }
    ) {
        // Phone hero: full-bleed cover from the very top of the screen (the
        // page hides the global top bar) with a floating back button that
        // respects the status-bar inset.
        if (mobile) {
            Box(modifier = Modifier.fillMaxWidth().styleModifier { property("position", "relative") }) {
                Img(
                    src = article.coverOrFallback(),
                    alt = article.title,
                    attrs = Modifier
                        .fillMaxWidth()
                        // Full width, natural height — whole cover, no crop.
                        .styleModifier {
                            property("height", "auto")
                            property("display", "block")
                        }
                        .toAttrs()
                )
                // Legibility scrim behind the status bar / back button.
                Div(attrs = {
                    style {
                        property("position", "absolute")
                        property("inset", "0 0 auto 0")
                        property("height", "96px")
                        property("background", "linear-gradient(180deg, rgba(2,4,11,0.55) 0%, rgba(2,4,11,0) 100%)")
                        property("pointer-events", "none")
                    }
                })
                Box(
                    modifier = Modifier
                        .mSize(36)
                        .mBorderRadius(100)
                        .cursor(Cursor.Pointer)
                        .onClick {
                            if (kotlinx.browser.window.history.length > 1) kotlinx.browser.window.history.back()
                            else ctx.router.navigateTo("/news")
                        }
                        .styleModifier {
                            property("position", "absolute")
                            property("top", "calc(env(safe-area-inset-top, 0px) + 12px)")
                            property("left", "16px")
                            property("background", "var(--l-glass-base, rgba(2, 10, 28, 0.55))")
                            property("backdrop-filter", "blur(12px)")
                            property("-webkit-backdrop-filter", "blur(12px)")
                            property("border", "0.3px solid rgba(180, 204, 246, 0.25)")
                        },
                    contentAlignment = Alignment.Center
                ) {
                    com.arena_web.ui.components.icons.Icon(
                        // Same back chevron the Chat top bar uses.
                        src = com.arena_web.ui.theme.AppIcons.ChevronBack,
                        modifier = Modifier.mSize(16).styleModifier {
                            property("filter", com.arena_web.ui.theme.ThemeVars.ICON_FILTER_V)
                        }
                    )
                }
            }
        }

        Column(
            modifier = Modifier
                .fillMaxWidth()
                .styleModifier {
                    if (!mobile) {
                        property("max-width", s(760))
                        property("margin", "0 auto")
                        property("padding-top", s(8))
                    } else {
                        // edge-to-edge page — the copy owns its side gutters.
                        property("padding", "14px 16px 0 16px")
                    }
                    property("gap", if (mobile) "14px" else s(18))
                }
        ) {
            // Desktop back row — same chevron+label pattern as the event
            // detail page's "Back to Events" (phones use the floating hero
            // button above instead).
            if (!mobile) {
                Row(
                    modifier = Modifier
                        .sGap(8)
                        .cursor(Cursor.Pointer)
                        .onClick {
                            if (kotlinx.browser.window.history.length > 1) kotlinx.browser.window.history.back()
                            else ctx.router.navigateTo("/news")
                        }
                        .styleModifier { property("width", "fit-content") },
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    com.arena_web.ui.components.icons.Icon(
                        src = com.arena_web.ui.theme.AppIcons.ChevronForward,
                        modifier = Modifier.styleModifier {
                            property("transform", "rotate(180deg)")
                        }
                    )
                    SpanText(
                        t.commonBack,
                        modifier = Modifier.bodyLMedium().color(com.arena_web.ui.theme.ThemeVars.Text2)
                    )
                }
            }

            // Desktop cover hero (phones render the full-bleed hero above).
            if (!mobile) Img(
                src = article.coverOrFallback(),
                alt = article.title,
                attrs = Modifier
                    .fillMaxWidth()
                    .sBorderRadius(24)
                    // Full width, natural height — whole cover, no crop.
                    .styleModifier {
                        property("height", "auto")
                        property("display", "block")
                    }
                    .toAttrs()
            )

            // Meta line: sport chip · date · views.
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                SportChip(article.sport, mobile)
                SpanText(
                    shortDate(article.publishedAt),
                    modifier = Modifier
                        .then(if (mobile) Modifier.mFontSize(13) else Modifier.sFontSize(14))
                        .color(com.arena_web.ui.theme.ThemeVars.Text3)
                )
            }

            // Title.
            SpanText(
                article.title,
                modifier = Modifier
                    .then(if (mobile) Modifier.mFontSize(22).mLineHeight(28) else Modifier.sFontSize(28).sLineHeight(36))
                    .fontWeight(700)
                    .color(com.arena_web.ui.theme.ThemeVars.Text1)
            )

            // Body — paragraphs split on blank lines.
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .styleModifier { property("gap", if (mobile) "12px" else s(14)) }
            ) {
                val paragraphs = article.body.ifBlank { article.summary }
                    .split("\n\n", "\n")
                    .map { it.trim() }
                    .filter { it.isNotEmpty() }
                paragraphs.forEach { paragraph ->
                    SpanText(
                        paragraph,
                        modifier = Modifier
                            .then(if (mobile) Modifier.mFontSize(15).mLineHeight(23) else Modifier.sFontSize(16).sLineHeight(26))
                            .color(com.arena_web.ui.theme.ThemeVars.Text2)
                    )
                }
            }

            // Hashtags — tap filters the news feed by that tag.
            if (article.hashtags.isNotEmpty()) {
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .styleModifier {
                            property("flex-wrap", "wrap")
                            property("gap", if (mobile) "8px" else s(8))
                        }
                ) {
                    article.hashtags.forEach { tag -> HashtagChip(tag, mobile) }
                }
            }

            // Linked match — highlight video + clickable score card.
            article.match?.let { ArticleMatchBlock(it, mobile) }

            // Related articles — two-tile grid (raw Div: Kobweb's Box is
            // itself a CSS grid that stacks children).
            if (related.isNotEmpty()) {
                SectionHeader(t.newsRelatedTitle, mobile)
                Div(
                    attrs = {
                        style {
                            property("display", "grid")
                            property("grid-template-columns", "repeat(2, 1fr)")
                            property("gap", if (mobile) "10px" else s(12))
                            property("width", "100%")
                        }
                    }
                ) {
                    related.forEach { RelatedCard(it, mobile) }
                }
            }

            // Trailing breathing room (no bottom nav on this surface).
            Div(attrs = {
                style {
                    property("width", "100%")
                    property("flex", "0 0 ${if (mobile) "48px" else "40px"}")
                    property("height", if (mobile) "48px" else "40px")
                }
            })
        }
    }
}

/** "#tag" pill — navigates to the feed filtered by that tag. */
@Composable
internal fun HashtagChip(tag: String, mobile: Boolean) {
    val ctx = rememberPageContext()
    val sportColors = com.arena_web.ui.theme.LocalSportColors.current
    Box(
        modifier = Modifier
            .cursor(Cursor.Pointer)
            .onClick { ctx.router.navigateTo("/news?tag=$tag") }
            .then(if (mobile) Modifier.mBorderRadius(100).mPadding(6, 12) else Modifier.sBorderRadius(100).sPadding(7, 14))
            .styleModifier {
                property("border", "0.3px solid transparent")
                property(
                    "background",
                    "${AppGradients.InteractiveSecondary} padding-box, " +
                        "linear-gradient(0deg, var(--arena-bg), var(--arena-bg)) padding-box, " +
                        "${AppGradients.ButtonBorderGradientPrimary} border-box"
                )
            },
        contentAlignment = Alignment.Center
    ) {
        SpanText(
            "#$tag",
            modifier = Modifier
                .then(if (mobile) Modifier.mFontSize(12) else Modifier.sFontSize(13))
                .fontWeight(500)
                .styleModifier { property("color", sportColors.primary.toString()) }
        )
    }
}

/** Related-article grid tile: cover on top, title + date below. */
@Composable
private fun RelatedCard(article: NewsArticleDto, mobile: Boolean) {
    val ctx = rememberPageContext()
    NavAnchor("/news/${article.id}", onNavigate = { ctx.router.navigateTo("/news/${article.id}") }) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .cursor(Cursor.Pointer)
                .then(if (mobile) Modifier.mBorderRadius(16) else Modifier.sBorderRadius(18))
                .overflow(Overflow.Hidden)
                .styleModifier {
                    property("height", "100%")
                    property("border", "0.5px solid ${AppGradients.NewsCardBorder}")
                    property("background", "var(--l-surface-2, var(--l-s2, #09142D))")
                }
        ) {
            Img(
                src = article.coverOrFallback(),
                alt = article.title,
                attrs = Modifier
                    .fillMaxWidth()
                    .then(if (mobile) Modifier.mHeight(92) else Modifier.sHeight(120))
                    // Fill the full cell width so grid tiles read as even cards;
                    // top-anchored so overflow crops from the bottom only.
                    .styleModifier {
                        property("object-fit", "cover")
                        property("object-position", "center top")
                        property("display", "block")
                    }
                    .coverImgAttrs(article.sport)
                    .toAttrs()
            )
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .then(if (mobile) Modifier.mPadding(10, 12).mGap(4) else Modifier.sPadding(12, 14).sGap(6))
            ) {
                SpanText(
                    article.title,
                    modifier = Modifier
                        .then(if (mobile) Modifier.mFontSize(13).mLineHeight(17) else Modifier.sFontSize(14).sLineHeight(19))
                        .fontWeight(600)
                        .color(com.arena_web.ui.theme.ThemeVars.Text1)
                        .then(lineClamp(2))
                )
                SpanText(
                    shortDate(article.publishedAt),
                    modifier = Modifier
                        .then(if (mobile) Modifier.mFontSize(11) else Modifier.sFontSize(12))
                        .color(com.arena_web.ui.theme.ThemeVars.Text3)
                )
            }
        }
    }
}
